import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { familyNames, femaleSurnames, maleSurnames } from '../data/names';

export default function Settings() {
  const { username } = useParams();
  const navigate = useNavigate();

  // Helper functions for namespaced localStorage
  const getStorage = (key) => username ? localStorage.getItem(`${username}-${key}`) : '';
  const setStorage = (key, value) => {
    if (!username) throw new Error('No username in URL');
    localStorage.setItem(`${username}-${key}`, value);
  };

  const [otherImage, setOtherImage] = useState(() => getStorage('otherImage') || '');
  const [otherName, setOtherName] = useState(() => getStorage('otherName') || '');
  const [expectedBehavior, setExpectedBehavior] = useState(() => getStorage('expectedBehavior') || 'Légy udvarias, válaszolj hosszan a kérdésekre. Kérdezz is vissza. Használj smiley-kat, de ne túl sokat.');
  const [gender, setGender] = useState('');
  const [appearance, setAppearance] = useState(() => getStorage('appearance') || '');
  const [personality, setPersonality] = useState(() => getStorage('personality') || '');

  // toast state + timer ref
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const toastTimerRef = useRef(null);

  // image generation / API state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);

  // API key input state (persisted locally)
  const [apiKeyInput, setApiKeyInput] = useState(() => localStorage.getItem('openaiApiKey') || '');

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const showToast = (message, type = 'success', duration = 3000) => {
    setToast({ visible: true, message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
      toastTimerRef.current = null;
    }, duration);
  };

  const saveName = (e) => {
    e.preventDefault();
    var generatedUsername = otherName.toLowerCase().replace(/\s+/g, '.');
    localStorage.setItem(`${generatedUsername}-otherName`, otherName);
    showToast('Name saved', 'success');
    navigate(`/settings/${generatedUsername}`);
  };

  const saveExpected = (e) => {
    e.preventDefault();
    if (!username) {
      showToast('No username in URL. Cannot save expected behavior.', 'error');
      return;
    }
    setStorage('expectedBehavior', expectedBehavior);
    showToast('Expected behavior saved', 'success');
  };

  const saveAppearance = (e) => {
    e.preventDefault();
    if (!username) {
      showToast('No username in URL. Cannot save appearance.', 'error');
      return;
    }
    setStorage('appearance', appearance);
    showToast('Appearance saved', 'success');
  };

  const saveApiKey = (e) => {
    e.preventDefault();
    if (!username) {
      showToast('No username in URL. Cannot save API key.', 'error');
      return;
    }
    localStorage.setItem('openaiApiKey', apiKeyInput || '');
    showToast(apiKeyInput ? 'API kulcs mentve' : 'API kulcs törölve', 'success');
  };

  const savePersonality = (e) => {
    e.preventDefault();
    if (!username) {
      showToast('No username in URL. Cannot save personality.', 'error');
      return;
    }
    setStorage('personality', personality);
    showToast('Personality saved', 'success');
  };

  const generateName = () => {
    if (!gender) {
      showToast('Please select a gender first', 'error');
      return;
    }

    const family = familyNames[Math.floor(Math.random() * familyNames.length)];
    const given = gender === 'male'
      ? maleSurnames[Math.floor(Math.random() * maleSurnames.length)]
      : femaleSurnames[Math.floor(Math.random() * femaleSurnames.length)];

    const generated = `${given} ${family}`;
    setOtherName(generated);
    showToast(`Generated name: ${generated}`, 'success', 4000);
  };

  // Generates a profile picture using OpenAI Responses image generation (reuses provided handleSubmit logic)
  const generateProfilePicture = async (e) => {
    setError('');
    setGeneratedImage(null);

    const apiKey = apiKeyInput || '';

    const prompt = appearance?.trim();

    if (!apiKey) {
      showToast('API kulcs megadása kötelező!', 'error');
      return;
    }
    if (!prompt) {
      showToast('A prompt megadása kötelező!', 'error');
      return;
    }

    setIsLoading(true);

    // Choose a random setting for the profile picture prompt
    const settings = [
      'The person is on a professional headshot photo, with a plain background, good lighting, and a friendly expression.',
      'This is a picture of himself/herself in a casual outdoor setting, with natural lighting and a genuine smile.',
      'The person is on a selfie in a modern urban environment, with stylish clothing and a confident pose.',
      'The person is on a portrait photo in a cozy indoor setting, with warm lighting and a relaxed expression.',
      'The person is taking a picture of themselves in the mirror while getting ready.'
    ];
    const setting = settings[Math.floor(Math.random() * settings.length)];

    try {
      const inputContent = [
        {
          role: "user",
          content: [
            { type: "input_text", text: `${prompt} ${setting} Do not put labels on it.` },
          ],
        },
      ];

      const payload = {
        model: "gpt-4o",
        input: inputContent,
        tools: [
          {
            type: "image_generation",
            quality: "high",
            size: "1024x1024"
          }
        ],
      };

      const response = await fetch("https://api.openai.com/v1/responses", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || "API hiba történt.");
      }

      const data = await response.json();

      const imageGenerationCalls = (data.output || []).filter(
        (output) => output.type === "image_generation_call"
      );
      const imageData = imageGenerationCalls.map((output) => output.result);

      if (imageData && imageData[0]) {
        const result = imageData[0];

        let dataUrl = null;
        if (result && typeof result === 'object' && result.b64_json) {
          dataUrl = `data:image/png;base64,${result.b64_json}`;
        } else if (result && typeof result === 'object' && result.image_url) {
          dataUrl = result.image_url;
        } else if (typeof result === 'string') {
          if (result.startsWith('data:image')) {
            dataUrl = result;
          } else {
            const base64Only = result.replace(/\s+/g, '');
            const isBase64 = /^[A-Za-z0-9+/=]+$/.test(base64Only);
            if (isBase64 && base64Only.length > 100) {
              dataUrl = `data:image/png;base64,${base64Only}`;
            }
          }
        } else if (Array.isArray(result) && result[0]?.b64_json) {
          dataUrl = `data:image/png;base64,${result[0].b64_json}`;
        }

        if (!dataUrl) {
          const raw = JSON.stringify(result || '');
          const maybeB64 = raw.match(/([A-Za-z0-9+/=]{100,})/);
          if (maybeB64 && maybeB64[1]) {
            dataUrl = `data:image/png;base64,${maybeB64[1]}`;
          }
        }

        if (!dataUrl) {
          let detailedError = data?.output?.[0]?.content?.[0]?.text;
          if (!detailedError) detailedError = data.error?.message || "Nem érkezett vissza generált kép.";
          setError(detailedError);
          setGeneratedImage(null);
          showToast(detailedError, 'error');
          setIsLoading(false);
          return;
        }

        setGeneratedImage(dataUrl);
        setOtherImage(dataUrl);
        setStorage('otherImage', dataUrl);
        showToast('Profilkép generálva és mentve', 'success', 4000);
      } else {
        let detailedError = data?.output?.[0]?.content?.[0]?.text;
        if (!detailedError) {
          detailedError = data.error?.message || "Nem érkezett vissza generált kép.";
        }
        setError(detailedError);
        setGeneratedImage(null);
        showToast(detailedError, 'error');
      }
    } catch (err) {
      const message = err.message || "Ismeretlen hiba történt.";
      setError(message);
      showToast(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generatePersonality = async () => {
    setError('');
    setIsLoading(true);

    const apiKey = apiKeyInput || '';
    if (!apiKey) {
      showToast('API kulcs megadása kötelező!', 'error');
      setIsLoading(false);
      return;
    }

    // You can customize the prompt as needed
    const prompt = `Kérlek, generálj egy részletes személyiségleírást egy fiktív ember számára, akit úgy hívnak, hogy ${otherName}. Írd le a hobbijait, érdeklődési köreit, és néhány jellemző tulajdonságát. Használj magyar nyelvet. Egybefüggő mondatokban fogalmazz, és férj bele 500 karakterbe. Ne írd bele a nevet a válaszba.`;

    const payload = {
      model: "gpt-4o",
      input: [
        {
          role: "user",
          content: [
            { type: "input_text", text: prompt },
          ],
        },
      ],
    };

    try {
      const response = await fetch("https://api.openai.com/v1/responses", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || "API hiba történt.");
      }

      const data = await response.json();
      const generatedText = data.output?.[0]?.content?.[0]?.text || '';

      if (generatedText) {
        setPersonality(generatedText);
        showToast('Personality generated!', 'success', 4000);
      } else {
        showToast('Nem sikerült személyiséget generálni.', 'error');
      }
    } catch (err) {
      const message = err.message || "Ismeretlen hiba történt.";
      setError(message);
      showToast(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const generateAppearance = async () => {
    setError('');
    setIsLoading(true);

    const apiKey = apiKeyInput || '';
    if (!apiKey) {
      showToast('API kulcs megadása kötelező!', 'error');
      setIsLoading(false);
      return;
    }

    // You can customize the prompt as needed
    const prompt = `Kérlek, generálj egy részletes külső leírást egy fiktív ember számára, akit úgy hívnak, hogy ${otherName}. A személyiségét a következők jellemzik: ${personality}. Írd le a haját, szemét, testalkatát, öltözködését, és egyéb külső jellemzőit. Használj angol nyelvet. Egybefüggő mondatokban fogalmazz, és férj bele 500 karakterbe. Ne írd bele a nevet a válaszba.`;

    const payload = {
      model: "gpt-4o",
      input: [
        {
          role: "user",
          content: [
            { type: "input_text", text: prompt },
          ],
        },
      ],
    };

    try {
      const response = await fetch("https://api.openai.com/v1/responses", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${apiKey}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || "API hiba történt.");
      }

      const data = await response.json();
      const generatedText = data.output?.[0]?.content?.[0]?.text || '';

      if (generatedText) {
        setAppearance(generatedText);
        showToast('Appearance generated!', 'success', 4000);
      } else {
        showToast('Nem sikerült külsőt generálni.', 'error');
      }
    } catch (err) {
      const message = err.message || "Ismeretlen hiba történt.";
      setError(message);
      showToast(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="card p-4">
        <div className="d-flex align-items-center mb-3">
          <button
            type="button"
            className="btn btn-link p-0 me-3"
            style={{ fontSize: '1.5em', textDecoration: 'none' }}
            onClick={() => {
              if (username) {
                navigate(`/chats/${username}`);
              } else {
                navigate('/chats');
              }
            }}
          >
            ⬅️
          </button>
        </div>
        { !!username ?
            <>
            <div className="d-flex justify-content-center mb-3">
            <img
                src={otherImage}
                alt=""
                className="rounded-circle"
                style={{ width: 160, height: 160, objectFit: 'cover' }}
            />
            </div>

            <h3 className="text-center mb-4">{otherName}</h3>
            </> :
            <form className="mb-3">
                <label className="form-label">Gender</label>
                <select
                    className="form-select"
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                >
                    <option value="">Select...</option>
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                </select>
            </form>
        }

        <form onSubmit={saveName} className="mb-3">
          <label className="form-label">Name</label>
          <input
            className="form-control"
            value={otherName}
            onChange={(e) => setOtherName(e.target.value)}
          />
          <div className="text-end mt-2">
            <button type="button" className="btn btn-secondary me-2" onClick={generateName}>Generate</button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>

        <form onSubmit={savePersonality}>
          <label className="form-label">Personality</label>
          <textarea
            className="form-control"
            rows="3"
            value={personality}
            onChange={(e) => setPersonality(e.target.value)}
          />
          <div className="text-end mt-2">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={generatePersonality}
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Generate'}
            </button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>

        <form onSubmit={saveAppearance}>
          <label className="form-label">Appearance</label>
          <textarea
            className="form-control"
            rows="3"
            value={appearance}
            onChange={(e) => setAppearance(e.target.value)}
          />
          <div className="text-end my-2">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={generateAppearance}
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Auto description'}
            </button>
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={generateProfilePicture}
              disabled={isLoading}
            >
              {isLoading ? 'Generating...' : 'Auto picture'}
            </button>
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>

        <form onSubmit={saveExpected} className="mb-3">
          <label className="form-label">Expected behavior in chat</label>
          <textarea
            className="form-control"
            rows="3"
            value={expectedBehavior}
            onChange={(e) => setExpectedBehavior(e.target.value)}
          />
          <div className="text-end mt-2">
            <button type="submit" className="btn btn-primary">Save</button>
          </div>
        </form>

        {/* API key form — placed at the bottom */}
        <form onSubmit={saveApiKey} className="mt-4">
          <label className="form-label">OpenAI API key</label>
          <div className="input-group">
            <input
              type="password"
              className="form-control"
              placeholder="sk-..."
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              aria-label="OpenAI API key"
            />
            <button className="btn btn-primary" type="submit">Save</button>
          </div>
          <div className="form-text mt-1">Stored locally in your browser (localStorage).</div>
        </form>
      </div>

      {/* Toast container - centered bottom. green for success, red for error */}
      <div aria-live="polite" aria-atomic="true" className="position-fixed bottom-0 start-50 translate-middle-x pb-4" style={{ zIndex: 1080 }}>
        <div
          className={`toast ${toast.visible ? 'show' : ''} align-items-center text-white ${toast.type === 'success' ? 'bg-success' : 'bg-danger'}`}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          style={{ minWidth: 280 }}
        >
          <div className="d-flex">
            <div className="toast-body">
              {toast.message}
            </div>
            <button type="button" className="btn-close btn-close-white me-2 m-auto" aria-label="Close" onClick={() => setToast((t) => ({ ...t, visible: false }))} />
          </div>
        </div>
      </div>
    </div>
  );
}